import { createSelector } from 'reselect';
import { RootState } from '../store';
import { TripState } from './trip.slice';
import { TopLevelItem } from './trip.types';
import { getList } from './trip.utils';

export const selectTripReducer = (state: RootState): TripState => state.trip;

export const selectTripsList = createSelector([selectTripReducer], (trip) => trip.trips);

export const selectTripCreationStatus = createSelector(
  [selectTripReducer],
  (trip) => trip.tripCreationStatus
);

export const selectError = createSelector([selectTripReducer], (trip) => trip.error);

export const selectCurrentTrip = createSelector([selectTripReducer], (trip) => trip.currentTrip);

export const selectActiveListId = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.activeListId : undefined
);

export const selectActiveList = createSelector([selectCurrentTrip], (trip) =>
  trip && trip.activeListId ? getList(trip.lists, trip.activeListId) : undefined
);

export const selectActiveListCardOrder = createSelector([selectActiveList], (list) =>
  list ? list.cardOrder : undefined
);

export const selectCurrentTripId = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.id : undefined
);

export const selectTripTitle = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.title : undefined
);

export const selectTripLocation = createSelector([selectCurrentTrip], (trip) =>
  trip ? { lat: trip.location.latitude, lng: trip.location.longitude } : undefined
);

export const selectLists = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.lists : undefined
);

export const selectListOrder = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.listOrder : undefined
);

export const selectDividers = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.dividers : undefined
);

// trip.listOrder is a mixed array of list IDs and divider IDs. This selector resolves
// each ID to its actual object so components can render without doing their own lookups.
export const selectOrderedTopLevelItems = createSelector(
  [selectCurrentTrip],
  (trip): TopLevelItem[] => {
    if (!trip) {
      return [];
    }
    return trip.listOrder
      .map((id): TopLevelItem | null => {
        const list = trip.lists.find((l) => l.id === id);
        if (list) {
          return { itemType: 'list', data: list };
        }
        const divider = trip.dividers.find((d) => d.id === id);
        if (divider) {
          return { itemType: 'divider', data: divider };
        }
        return null;
      })
      .filter((item): item is TopLevelItem => item !== null);
  }
);

export const selectCards = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.cards : undefined
);

export const selectMapCenter = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.mapCenter : undefined
);

export const selectMapZoom = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.mapZoom : undefined
);

export const selectUserEmails = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.userEmails : undefined
);

export const selectDays = createSelector([selectCurrentTrip], (trip) => (trip ? trip.days : {}));

export const selectEvents = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.events : {}
);

export const selectDeletedEvents = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.deletedEvents : undefined
);

export const selectChangedDefaultDates = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.isChangedDefaultDates : undefined
);

export const selectLastOpened = createSelector([selectCurrentTrip], (trip) =>
  trip ? trip.lastOpened : undefined
);
